﻿import React, { useState, useEffect, useCallback } from 'react';
import { Button as BPButton, MenuItem } from "@blueprintjs/core";
import { Select } from "@blueprintjs/select";
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { FormHelperText } from "components/form-fields";
import 'components/form-fields/FormFields.css';
import { ShowHide } from 'components/layout';
import { Tooltip } from 'components/tooltip';
import { Button } from 'components/buttons';

const defaultArray = [];

export function FormSelect(
    {
        id = null,
        placeholder = "Choose an item",
        items = defaultArray,
        loading = false,
        headingText = "",
        helperText = null,
        disabled = false,
        selectedValue = null,
        dangerHelperText = null,
        scrollable = false,
        small = false,
        fill = false,
        unit = null,
        dynamicWidth,
        onItemSelect
    }
) {
	const [selectedItem, setSelectedItem] = useState(null);
	
	useEffect(setItemFromValue, [selectedValue, items]);
	function setItemFromValue() {
		var newItem = items.find(obj => obj.id === selectedValue);
		setSelectedItem(newItem);
	}

	const renderSelect = useCallback(() => {
		function renderSelectItem(item, { handleClick, modifiers }) {
			return (
				<MenuItem
					active={modifiers.active}
					onClick={handleClick}
					key={item.id}
					text={item.name}
					label={item.label}
					disabled={item.disabled || modifiers.disabled}
				/>
			);
		}

		return (
            <Select
                className={classNames({ "bp6-skeleton": loading, "small": small, "fill": fill })}
                    items={items}
                    onItemSelect={onItemSelect}
                    itemRenderer={renderSelectItem}
                    filterable={false}
                    popoverProps={{ minimal: true, placement: "bottom-start", boundary: "window", usePortal: true }}
                    activeItem={selectedItem}
                    disabled={disabled}
                ><BPButton text={selectedItem ? selectedItem.name : placeholder} disabled={disabled} endIcon="double-caret-vertical" className={classNames({
                    "fixed-width": !dynamicWidth,
                    "bp6-skeleton": loading,
                    "small": small
                })} />
                </Select>
        );
	}, [items, onItemSelect, selectedItem, placeholder, loading, disabled, dynamicWidth]);

	function selectPrevItem() {
		var currentIndex = items.findIndex(obj => obj.id === selectedItem.id);
		currentIndex--;

		if (currentIndex < 0) {
			currentIndex = items.length - 1;
		}

		setSelectedItem(items[currentIndex]);
		onItemSelect(items[currentIndex]);
	}
	
	function selectNextItem() {
		var currentIndex = items.findIndex(obj => obj.id === selectedItem.id);
		currentIndex++;

		if (currentIndex > items.length - 1) {
			currentIndex = 0;
		}

		setSelectedItem(items[currentIndex]);
		onItemSelect(items[currentIndex]);
	}

	return (
        <div className="form-field form-field-select" id={id}>
            <ShowHide
				evaluator={headingText.length > 0}
				show={(
					<h4 className={classNames({ "bp6-skeleton": loading })}>{headingText}</h4>
				)}
			/>
            <FormHelperText loading={loading}>{helperText}</FormHelperText>
            <ShowHide
				evaluator={scrollable}
				show={(
					<div className="inline-items">
						<Tooltip content="Previous">
							<Button minimal icon="fast-backward" intent="secondary" iconOnly disabled={items.length === 0 || selectedItem == null || disabled} loading={loading} onClick={selectPrevItem} />
						</Tooltip>
						<div className='inline-items'>
							{renderSelect()}
							{unit && (
								typeof unit === 'string' ? <span>{unit}</span> : unit
							)}
						</div>
						<Tooltip content="Next">
							<Button minimal icon="fast-forward" intent="secondary" iconOnly disabled={items.length === 0 || selectedItem == null || disabled} loading={loading} onClick={selectNextItem} />
						</Tooltip>
					</div>
				)}
				hide={
					<div className='inline-items'>
						{renderSelect()}
						{unit && (
							typeof unit === 'string' ? <span>{unit}</span> : unit
						)}
					</div>
				}
			/>
            <FormHelperText loading={loading} danger={true}>{dangerHelperText}</FormHelperText>
        </div>
    );
}


FormSelect.propTypes = {
	id: PropTypes.string,
	placeholder: PropTypes.string,
	items: PropTypes.array,
	onItemSelect: PropTypes.func.isRequired,
	dynamicWidth: PropTypes.bool,
	loading: PropTypes.bool,
	headingText: PropTypes.string,
	helperText: PropTypes.node,
	disabled: PropTypes.bool,
	selectedValue: PropTypes.any,
	dangerHelperText: PropTypes.node,
	scrollable: PropTypes.bool,
	small: PropTypes.bool,
	fill: PropTypes.bool,
	unit: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.node
	])
};